/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { SessionErrorResponseCodeEnum } from 'client-code/session-manager';
import { SubEvent } from 'sub-events';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SessionErrorMapper } from '../error/SessionErrorMapper';
import { IPlaybackError, IStreamingSession, TSessionCreateModel } from '../interfaces';
import { IServices } from '../../interfaces/IServices';
import { ServicesClientImpl } from '../../impls/ServicesClientImpl';
import { Session } from '../../entities/Session';
import { UniqueIdentifier } from '../../utils/UniqueIdentifier';
import { Log, VuLogger, QoEAppLogger, OperationsLogger } from '../../logging';
import { LiveTvPlaybackError, LiveTvSessionError } from '../error';
import { PlaybackError } from '../../error/PlaybackError';
import { AppError } from '../../error/AppError';
import { TSessionState } from '../../types';
import { PlaybackErrorType } from '../enums';

export abstract class StreamingSessionImpl implements IStreamingSession {
    TAG: string = 'StreamingSessionImpl';

    private static mSessionStateChangeCallback: Function = () => null;

    protected services: IServices = new ServicesClientImpl();

    protected oSession = {} as Session;

    protected timeoutId: any;

    public onSessionCreated: SubEvent<Session> = new SubEvent();

    protected SESSION_MANAGER_RETRY_ON_ERROR_MESSAGE = [
        SessionErrorResponseCodeEnum.ExternalError,
        SessionErrorResponseCodeEnum.HostUnavailable,
        SessionErrorResponseCodeEnum.InternalError,
        SessionErrorResponseCodeEnum.NoSession,
        SessionErrorResponseCodeEnum.UnknownLocation,
    ];

    protected SESSION_MANAGER_DEFAULT_RETRY_OPTIONS = {
        retryOn: {
            '400': {
                delayType: 'incremental',
                delay: [2000, 3000, 5000, 7000, 11000, 13000],
                errorMessage: this.SESSION_MANAGER_RETRY_ON_ERROR_MESSAGE,
            },
        },
    };

    // eslint-disable-next-line no-unused-vars
    abstract createStreamingSession(sessionCreateModel: TSessionCreateModel): void;

    // eslint-disable-next-line class-methods-use-this
    private onSessionStateChangeHandler(error: IPlaybackError): void {
        StreamingSessionImpl.mSessionStateChangeCallback(error);
    }

    // eslint-disable-next-line class-methods-use-this
    public addSessionStateChangeListener(callback: Function): void {
        StreamingSessionImpl.mSessionStateChangeCallback = callback;
    }

    sessionKeepAlive(): void {
        const { sessionId } = this.oSession;
        if (sessionId) {
            this.services
                .sessionKeepAlive(this.oSession, UniqueIdentifier.getNextSessionNetworkCallId())
                .then((response: TSessionState) => {
                    if (
                        response.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()
                    ) {
                        Diagnostics.debug(
                            this.TAG,
                            `{sessionKeepAlive: ${JSON.stringify(response)}}`,
                        );
                        Log.d(this.TAG, `{sessionKeepAlive: ${JSON.stringify(response)}}`);
                    }
                })
                .catch((error) => {
                    if (error.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()) {
                        clearTimeout(this.timeoutId);
                        clearInterval(this.timeoutId);
                        if (error.response) this.handleSessionError(error);
                        Diagnostics.error(this.TAG, `sessionKeepAlive:${error}`);
                        Log.e(this.TAG, `sessionKeepAlive:${error}`);
                    }
                });
        }
    }

    sessionStop(): void {
        const { sessionId } = this.oSession;
        clearTimeout(this.timeoutId);
        clearInterval(this.timeoutId);
        if (sessionId) {
            this.services
                .sessionDelete(this.oSession, UniqueIdentifier.getNextSessionStopNetworkCallId())
                .then((response: TSessionState) => {
                    if (
                        response.sessionNetworkCallId ===
                        UniqueIdentifier.getSessionStopNetworkCallId()
                    ) {
                        Diagnostics.debug(this.TAG, `sessionStop${JSON.stringify(response)}`);
                        Log.d(this.TAG, `sessionStop${JSON.stringify(response)}`);
                    }
                })
                .catch((error) => {
                    if (
                        error.sessionNetworkCallId ===
                        UniqueIdentifier.getSessionStopNetworkCallId()
                    ) {
                        Diagnostics.error(this.TAG, `sessionStop:${error}`);
                        Log.e(this.TAG, `sessionStop:${error}`);
                    }
                })
                .finally(() => {
                    this.oSession = {} as Session;
                });
        }
    }

    handlePlaybackError = (error: any) => {
        const playbackError: LiveTvPlaybackError = new LiveTvPlaybackError(
            new PlaybackError(error),
        );
        Diagnostics.debug(this.TAG, `handlePlaybackError {playbackError: `, playbackError, '}');
        Log.d(this.TAG, `handlePlaybackError {playbackError: `, playbackError, '}');
        QoEAppLogger.logPlaybackError(playbackError);
        OperationsLogger.logUIErrorCode(playbackError.getUiErrorCode());
        this.onSessionStateChangeHandler({
            type: PlaybackErrorType.LiveTvSessionError,
            data: playbackError,
        });
    };

    handleSessionError = (error: any) => {
        if (error && error.response && error.response.data) {
            const sessionError: LiveTvSessionError = new LiveTvSessionError(
                new AppError(error.response.data.code, error.response.data.message),
            );
            QoEAppLogger.logSessionError(sessionError);
            OperationsLogger.logUIErrorCode(sessionError.getUiErrorCode());
            const sessionErrorType = SessionErrorMapper.mapSessionErrorType(
                error.response.data.code,
            );
            VuLogger.logErrorEvent({
                code: error.response.data.code,
                message: error.response.data.message,
                type: sessionErrorType,
                source: VuLogger.ErrorEventSource.STREAMING_SESSION_ERROR,
            });
            VuLogger.logNotifyEvent(
                VuLogger.NotifyEventSourceType.ERROR_OVERLAY,
                VuLogger.NotifyEventSourceName.SESSION_ERROR,
            );
            Diagnostics.info(this.TAG, `handleSessionError`, JSON.stringify(sessionError));
            Log.i(this.TAG, `handleSessionError`, JSON.stringify(sessionError));
            this.onSessionStateChangeHandler({
                type: PlaybackErrorType.LiveTvSessionError,
                data: sessionError,
            });
        }
    };

    getSessionInfo(): Session {
        return this.oSession;
    }

    setKeepAliveInterval = (keepAliveIntervalSeconds: number) => {
        Diagnostics.info(
            this.TAG,
            `setKeepAliveInterval: keepAliveIntervalSeconds=`,
            keepAliveIntervalSeconds,
        );
        Log.i(
            this.TAG,
            `setKeepAliveInterval: keepAliveIntervalSeconds=`,
            keepAliveIntervalSeconds,
        );
        clearTimeout(this.timeoutId);
        clearInterval(this.timeoutId);
        this.sessionKeepAlive();
        this.timeoutId = setInterval(() => {
            this.sessionKeepAlive();
        }, keepAliveIntervalSeconds);
    };
}
